import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { t } from '../../i18n'
import submitNavigationForm from '../shared/versioning/submitNavigationForm'
import extractServerErrors from '../shared/extractServerErrors'
import {
  AI_SUGGESTION_FIELDS,
  AiSuggestion,
  AiSuggestionField,
  AiSuggestionMove
} from './models'

const FIELD_LABELS: Record<AiSuggestionField, string> = {
  position_mm: 'pos',
  velocity_m_s: 'vel',
  acceleration_m_s2: 'acc',
  deceleration_m_s2: 'dec',
  pause_time_ms: 'pause'
}

interface AiSuggestionPanelProps {
  servoMoveStepId: number
}

interface PendingSuggestion {
  id: number
  conversation_id: string
}

type SuggestionStatus =
  | { state: 'pending' }
  | { state: 'failed'; error: string }
  | ({ state: 'done' } & AiSuggestion)

// The platform answers through a callback, so the panel polls until the request is done
const POLL_INTERVAL_MS = 3000
const POLL_LIMIT_MS = 5 * 60 * 1000

const AiSuggestionPanel: React.FC<AiSuggestionPanelProps> = ({
  servoMoveStepId
}) => {
  const [instruction, setInstruction] = useState('')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [suggestion, setSuggestion] = useState<AiSuggestion | null>(null)
  const [pending, setPending] = useState<PendingSuggestion | null>(null)
  const conversationId = useRef<string | null>(null)

  useEffect(() => {
    if (!pending) return undefined

    const startedAt = Date.now()
    const finish = (result: SuggestionStatus | string[]) => {
      setPending(null)
      setLoading(false)
      if (Array.isArray(result)) {
        setErrors(result)
      } else if (result.state === 'done') {
        setSuggestion(result)
        setInstruction('')
      } else if (result.state === 'failed') {
        setErrors([result.error])
      }
    }

    const timer = setInterval(() => {
      if (Date.now() - startedAt > POLL_LIMIT_MS) {
        finish([t('servo_move_steps.ai_suggestions.still_processing')])
        return
      }

      axios
        .get<SuggestionStatus>(`/admin/ai_suggestion_requests/${pending.id}`)
        .then((resp) => {
          if (resp.data.state !== 'pending') finish(resp.data)
        })
        .catch((error) =>
          finish(
            extractServerErrors(
              error,
              t('servo_move_steps.ai_suggestions.failed')
            )
          )
        )
    }, POLL_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [pending])

  const generate = () => {
    if (instruction.trim() === '' || loading) return

    setLoading(true)
    setErrors([])
    axios
      .post<PendingSuggestion>(
        `/admin/servo_move_steps/${servoMoveStepId}/ai_suggestion`,
        {
          instruction,
          conversation_id: conversationId.current
        }
      )
      .then((resp) => {
        conversationId.current = resp.data.conversation_id
        setPending(resp.data)
      })
      .catch((error) => {
        setLoading(false)
        setErrors(
          extractServerErrors(
            error,
            t('servo_move_steps.ai_suggestions.failed')
          )
        )
      })
  }

  const discardSuggestion = () => {
    if (!suggestion) return
    if (!window.confirm(t('servo_move_steps.delete_draft_confirm_title')))
      return

    submitNavigationForm(
      `/admin/servo_move_steps/${suggestion.draft_id}`,
      'delete'
    )
  }

  const renderValues = (move: AiSuggestionMove, before?: AiSuggestionMove) => {
    const fields = before
      ? AI_SUGGESTION_FIELDS.filter((field) => before[field] !== move[field])
      : AI_SUGGESTION_FIELDS
    if (fields.length === 0) return null

    return (
      <div key={move.step_number} className="ai-suggestion__move">
        <strong>#{move.step_number}</strong>{' '}
        {fields.map((field) => (
          <span key={field} className="me-2">
            {FIELD_LABELS[field]}: {before ? <>{before[field]} → </> : null}
            <strong>{move[field]}</strong>
          </span>
        ))}
      </div>
    )
  }

  // Moves are renumbered after a removal, so once the count changes a per-move diff
  // would pair unrelated moves; the suggestion is shown in full instead
  const renderDiff = (current: AiSuggestion) => {
    if (current.before.length !== current.after.length) {
      return (
        <>
          <div className="ai-suggestion__move">
            {t('servo_move_steps.ai_suggestions.moves_count', {
              before: current.before.length,
              after: current.after.length
            })}
          </div>
          {current.after.map((move) => renderValues(move))}
        </>
      )
    }

    return current.after.map((move) =>
      renderValues(
        move,
        current.before.find((entry) => entry.step_number === move.step_number)
      )
    )
  }

  return (
    <div className="mb-3">
      <h5 className="mb-2">{t('servo_move_steps.ai_suggestions.title')}</h5>
      <div className="ai-suggestion">
        {errors.length > 0 && (
          <div className="alert alert-danger" role="alert">
            {errors.join(', ')}
          </div>
        )}
        {suggestion && (
          <div className="mb-3">
            <div className="alert alert-info mb-2 ai-suggestion__reason">
              {suggestion.reason}
            </div>
            {renderDiff(suggestion)}
            {suggestion.clamped_to_safe_limits.length > 0 && (
              <div className="mt-2 ai-suggestion__clamped">
                {t('servo_move_steps.ai_suggestions.clamped_notice')}{' '}
                {suggestion.clamped_to_safe_limits
                  .map(
                    (clamped) =>
                      `#${clamped.step_number} ${FIELD_LABELS[clamped.field]}: ${clamped.requested} → ${clamped.applied}`
                  )
                  .join(', ')}
              </div>
            )}
            {suggestion.affected_move_modes.length > 0 && (
              <div className="mt-1 ai-suggestion__affected">
                {t('servo_move_steps.used_by_move_modes')}:{' '}
                {suggestion.affected_move_modes.join(', ')}
              </div>
            )}
            <div className="ai-suggestion__actions">
              <a
                className="btn btn-primary btn-sm"
                href={`/admin/servo_move_steps/${suggestion.draft_id}/edit`}
              >
                {t('servo_move_steps.ai_suggestions.open_draft', {
                  version: suggestion.draft_version
                })}
              </a>
              <button
                type="button"
                className="btn btn-danger btn-sm inverted"
                onClick={discardSuggestion}
              >
                {t('servo_move_steps.ai_suggestions.discard')}
              </button>
            </div>
          </div>
        )}
        <textarea
          className="form-control mb-2"
          rows={2}
          value={instruction}
          placeholder={
            suggestion
              ? t('servo_move_steps.ai_suggestions.refine_placeholder')
              : t('servo_move_steps.ai_suggestions.placeholder')
          }
          onChange={(e) => setInstruction(e.target.value)}
        />
        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={loading || instruction.trim() === ''}
            onClick={generate}
          >
            {loading
              ? t('servo_move_steps.ai_suggestions.generating')
              : t('servo_move_steps.ai_suggestions.generate')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AiSuggestionPanel
